/**
 * useDashboard-helpers — sub-functions for useDashboard to keep it under the
 * lint cap. Behaviour unchanged.
 */

import { type DashboardEntry } from './dashboard-helpers'
import { getCurrentPeriod, type FiscalPeriod } from '../../services/fiscal-period-service'
import { listJournalEntries } from '../../services/journal-entry-service'
import {
  getLocalJournalEntries,
  getLocalTrialBalance,
  getLocalFiscalPeriods,
  type LocalFiscalPeriod,
} from '../../services/local-db'
import { getTrialBalance, type TrialBalanceReport } from '../../services/reporting-service'
import { toLocalDateStr } from '../../utils/formatters'

// ── Local data loading ────────────────────────────────────────────────────

function localPeriodToFiscalPeriod(p: LocalFiscalPeriod): FiscalPeriod {
  return {
    id: p.id, orgId: p.orgId, fiscalYear: p.fiscalYear, periodType: p.periodType,
    periodNumber: p.periodNumber, name: p.name, startDate: p.startDate, endDate: p.endDate,
    status: p.status, closedAt: p.closedAt, closedBy: p.closedBy,
    audit: { createdAt: p.createdAt, createdBy: '', updatedAt: p.createdAt, updatedBy: '' },
  }
}

function mapLocalEntries(localEntries: Awaited<ReturnType<typeof getLocalJournalEntries>>, currency: string): DashboardEntry[] {
  return localEntries.slice(0, 5).map(e => ({
    id: e.id,
    entryNumber: e.entryNumber,
    entryDate: e.entryDate,
    memo: e.memo ?? '',
    totalDebit: {
      amount: (e.lines ?? []).reduce((sum, l) => sum + (l.debitAmount ?? 0), 0),
      currency,
    },
  }))
}

function buildLocalTrialBalance(localTb: ReturnType<typeof getLocalTrialBalance>, currency: string): TrialBalanceReport {
  return {
    asOfDate: localTb.asOfDate,
    generatedAt: localTb.generatedAt,
    rows: (localTb.rows ?? []).map(r => ({
      accountId: r.accountId,
      accountCode: r.accountCode,
      accountName: r.accountName,
      accountType: r.accountType,
      debitBalance: { amount: r.debitBalance, currency },
      creditBalance: { amount: r.creditBalance, currency },
    })),
    totalDebit: { amount: localTb.totalDebit, currency },
    totalCredit: { amount: localTb.totalCredit, currency },
    isBalanced: localTb.isBalanced,
  }
}

export interface LoadLocalSetters {
  setRecentEntries: (entries: DashboardEntry[]) => void
  setTrialBalance: (tb: TrialBalanceReport) => void
  setCurrentPeriod: (p: FiscalPeriod) => void
}

export async function loadLocalDashboardData(
  organizationId: string,
  currentLedgerId: string | null,
  currency: string,
  setters: LoadLocalSetters,
): Promise<void> {
  const localEntries = await getLocalJournalEntries(organizationId, currentLedgerId ?? undefined)
  setters.setRecentEntries(mapLocalEntries(localEntries, currency))

  const today = toLocalDateStr(new Date())
  const localTb = getLocalTrialBalance(organizationId, {
    ...(currentLedgerId && { ledgerId: currentLedgerId }),
    asOfDate: today,
  })
  setters.setTrialBalance(buildLocalTrialBalance(localTb, currency))

  const localPeriods = await getLocalFiscalPeriods(organizationId, currentLedgerId ?? undefined)
  const currentPd = localPeriods.find(p => p.status === 'PERIOD_STATUS_OPEN')
  if (currentPd) {
    setters.setCurrentPeriod(localPeriodToFiscalPeriod(currentPd))
  }
}

// ── API loading ──────────────────────────────────────────────────────────

export interface ApiLoadSetters {
  setRecentEntries: (entries: DashboardEntry[]) => void
  setCurrentPeriod: (p: FiscalPeriod) => void
  setTrialBalance: (tb: TrialBalanceReport) => void
}

/**
 * Returns true if API call succeeded (entries returned).
 */
export async function loadApiDashboardData(
  organizationId: string,
  setters: ApiLoadSetters,
): Promise<boolean> {
  try {
    const [entriesRes, periodRes, tbRes] = await Promise.all([
      listJournalEntries(organizationId, {}, 5),
      getCurrentPeriod(organizationId),
      getTrialBalance(organizationId),
    ])

    let isApiSuccess = false
    if (entriesRes.data) {
      setters.setRecentEntries(
        (entriesRes.data.entries ?? []).map(e => ({
          id: e.id,
          entryNumber: e.entryNumber,
          entryDate: e.entryDate,
          memo: e.memo ?? '',
          totalDebit: { ...e.totalDebit, amount: Number(e.totalDebit.amount) },
        })),
      )
      isApiSuccess = true
    }
    if (periodRes.data) { setters.setCurrentPeriod(periodRes.data) }
    if (tbRes.data) { setters.setTrialBalance(tbRes.data) }
    return isApiSuccess
  } catch {
    return false
  }
}
